/**
 * Generic offline overlay for repeating-section collections (#996 Phase 2.5).
 *
 * The pending-operation overlay (apply create/update/delete/reorder ops onto a
 * cached base list, in timestamp order, de-duping optimistic creates by id) is
 * identical across sections; only the per-op field names (`<section>Id` /
 * `<section>Ids`) differ. Section modules pass selectors for those.
 *
 * The thin cache read/write wrappers (getCached*, mirror*, getPending*) stay in
 * each section module because they are bound to section-specific offlineCache
 * method names.
 */

/** Minimal unit shape the overlay needs to write synthesized creates. */
export interface RepeatableOfflineUnit {
  id?: number;
  requestId: number;
  /**
   * Existing/new status. Optional because sections name this column differently
   * (`status` for Small UPS et al., `rackStatus` for Racks); the write key is
   * chosen via `RepeatableOverlayOptions.statusField` (defaults to `status`).
   */
  status?: 'existing' | 'new';
  displayOrder: number;
}

/** Overlay tuning — currently just the status column name for synthesized creates. */
export interface RepeatableOverlayOptions {
  /** Unit field the create branch writes the existing/new default onto. Default `status`. */
  statusField?: string;
}

/** Minimal pending-operation shape shared by every section's offline queue. */
export interface RepeatableOfflineOperation<TUnit> {
  operation: 'create' | 'update' | 'delete' | 'reorder';
  timestamp: number;
  data?: Partial<TUnit>;
}

/** Selectors that read the section-specific instance id / reorder ids off an op. */
export interface RepeatableOfflineOpSelectors<TOp> {
  instanceId: (op: TOp) => number | undefined | null;
  reorderIds: (op: TOp) => number[] | undefined;
}

/**
 * Overlay pending offline operations onto a base list so offline reads reflect
 * not-yet-synced changes. Pure and deterministic (ops applied in timestamp
 * order). De-dupes creates by id so an optimistic unit already written into the
 * base list is not duplicated (mirrors #995 bug 6).
 */
export function applyPendingRepeatableOperations<
  TUnit extends RepeatableOfflineUnit,
  TOp extends RepeatableOfflineOperation<TUnit>,
>(
  requestId: string,
  base: TUnit[],
  ops: TOp[],
  selectors: RepeatableOfflineOpSelectors<TOp>,
  options?: RepeatableOverlayOptions,
): TUnit[] {
  const statusField = options?.statusField ?? 'status';
  const ordered = [...ops].sort((a, b) => a.timestamp - b.timestamp);
  let result = [...base];

  for (const op of ordered) {
    switch (op.operation) {
      case 'create': {
        if (!op.data) {
          break;
        }
        const id = selectors.instanceId(op);
        if (id != null && result.some(unit => unit.id === id)) {
          break; // already present in base (cached optimistic write)
        }
        const dataRecord = op.data as Record<string, unknown> | undefined;
        result.push({
          ...(op.data as TUnit),
          id: id ?? op.timestamp,
          requestId: Number(requestId),
          [statusField]: dataRecord?.[statusField] ?? 'new',
          displayOrder: op.data?.displayOrder ?? result.length + 1,
        } as TUnit);
        break;
      }
      case 'update': {
        const id = selectors.instanceId(op);
        if (id == null || !op.data) {
          break;
        }
        result = result.map(unit =>
          unit.id === id ? { ...unit, ...op.data } : unit,
        );
        break;
      }
      case 'delete': {
        const id = selectors.instanceId(op);
        if (id == null) {
          break;
        }
        result = result.filter(unit => unit.id !== id);
        break;
      }
      case 'reorder': {
        const ids = selectors.reorderIds(op);
        if (!ids) {
          break;
        }
        const byId = new Map(result.map(unit => [unit.id, unit]));
        const reordered = ids
          .map(id => byId.get(id))
          .filter((unit): unit is TUnit => unit !== undefined);
        const remaining = result.filter(
          unit => !ids.includes(unit.id as number),
        );
        result = [...reordered, ...remaining];
        break;
      }
    }
  }

  return result;
}
